<template>
    <div class="table-wrapper">
        <table class="table-data" :style="tableStyleChange" :class="tableClass">
            <thead class="table-thead">
                <slot name="header">
                    <tag-table-row>
                        <tag-table-header v-for="header in headers"
                                        :key="header.key"
                                        :title="header.title"
                                        :class="[headerClass, headerClasses[header.key], header.sortable?'sortable':'']"
                                        :style="{width: header.width>0?`${header.width}px`:'auto'}"
                                        @click-header="()=>onHeaderClicked(header)"
                        />
                    </tag-table-row>
                </slot>
            </thead>
            <tbody>
                <tag-table-row v-for="(item, row) in sortedItems"
                             :key="createRowKey(row)"
                               @click-row="()=>onRowClicked(item)"

                >
                    <tag-table-cell v-for="(header, col) in headers"
                                  :ref="`cell:${row}:${col}`"
                                  :key="createCellKey(row,col,header.key)"
                                  :title="header.shortText?item[header.key]:''"
                                  :class="[cellClass, cellClasses[header.key]]"
                                    :style="header.shortText && 'short-text'"
                                  @click-cell="()=>onCellClicked(item)"
                    >
                        <slot :name="`cell-${header.key}`" :item="item">
                            {{ dataFormatting(item[header.key],header.type) }}
                        </slot>
                    </tag-table-cell>
                </tag-table-row>
            </tbody>
            <v-btn
                v-if="useAddLoadButton"
                :color="disableAddButton ? 'primary':'#cccccc'"
                dark
                large
                @click="clickAdditionalLoadButton"
                class="add-load-button"
            >
                추가로드
            </v-btn>
        </table>

    </div>

</template>

<script>
/**
 * 태그로 구성된 공통 테이블 컴포넌트입니다.
 * header에 sorting하는 기능은 아직 테스트 중이고 , select 하는 기능은 구현되지 않았습니다.
 * 금액, 날짜 속성 데이터같은 경우 headerDataList.js 에서 type 속성을 주면 됩니다.
 * 개인적으로 cell을 컨트롤 하고 싶으면 #cell-0000(headerDataList.js에서 지정해준 key값 )을 주면 됩니다.
 *
 */
import TagTableHeader from "@/components/TagTable/TagTableHeader";
import TagTableRow from "@/components/TagTable/TagTableRow";
import TagTableCell from "@/components/TagTable/TagTableCell";
import {DateTime} from "luxon";


export default {
    name: "TagTable",
    components: {TagTableCell, TagTableRow, TagTableHeader},
    props: {
        headers: {//header에 들어갈 데이터입니다.
            type: [Array],
            default: () => []
        },
        items: { //body에 들어갈 데이터입니다.
            type: [Array],
            default: () => []
        },
        itemKey: {type: String, default: undefined},//어떤 테이블인지 key를 부여합니다.
        selectable: {type: Boolean, default: false},//첫 열에 select를 할지 여부입니다.
        selectableType: {type: String, default: 'single', validator: (v) => ['single', 'multiple'].includes(v)}, //select를 single로 할지 multy로 할지
        sort: {type: Boolean, default: false}, //정렬할지 말지
        sortHandler: {type: Function, default: undefined},//정렬을 내부함수로 할수있지만 외부함수로도 할수있게 했습니다.
        headerClass: {type: String, default: undefined},//css속성을 컨트롤 하기 위함입니다.
        headerClasses: {type: Object, default: () => ({})},//css속성을 컨트롤 하기 위함입니다.
        cellClass: {type: String, default: undefined},//css속성을 컨트롤 하기 위함입니다.
        cellClasses: {type: Object, default: () => ({})},//css속성을 컨트롤 하기 위함입니다.
        tableClass: {type: String, default: 'width:120%'},//100%를 주면 스크롤이 작동안하고 이상으로 주면 작동합니다.,
        tableStyle:{type: Object, default: () => ({})},
        useAddLoadButton:{type:Boolean,default:false},//추가로드 버튼 사용여부
        disableAddButton:{type:Boolean,default:false}//추가로드 버튼 비활성화하는 여부입니다.
    },
    data() {
        return {
            selectedItemPositions:undefined,
            sortField: undefined,
            sortDir: undefined,
            sortDirection: {}
        }
    },
    watch: {
        selectedItemPositions(newValue, oldValue) {
            console.dir(newValue)
        }
    },
    computed: {
        sortedItems() {
            // 정렬할 필드가 지정되지 않으면 정렬을 수행하지 않는다.
            if (this.sortField === undefined) return this.items
            if (this.sortDir === undefined) return this.items
            return this.items.slice(0).sort((a, b) => {
                const f = this.sortHandler || this.internalSortFunction
                const originResult = f(a, b);
                const sortAdjustValue = this.sortDir === "asc" ? 1 : -1;
                return originResult * sortAdjustValue
            })
        },
        tableStyleChange(){
            const tableWith = this.tableStyle.width>0?`${this.tableStyle.width}%`:'120%'
            const tableLayout = this.tableStyle.tableLayout||'auto'
            return {width:tableWith ,tableLayout}
        }

    },
    methods: {
        onRowClicked(item) {
            this.$emit("click-row",item)
        },
        onCellClicked(item) {
            this.$emit("click-cell",item)
        },
        clickAdditionalLoadButton(){
            if(!this.disableButton) return //데이터가 없을때 추가로드 비활성화 합니다.
            this.$emit("click-add-load-button")
        },
        /**
         * type에 따라서 데이터를 formmating 합니다
         * 1. number : 3번째 자리에 ,를 찍어줍니다.
         * 2. date : UTC+9로 바꿔줍니다.
         * @param cellValue
         * @param type
         * @returns {string|*}
         */
        dataFormatting(cellValue,type) {
            if(type ==='date') {
                return this.dateFormatChange(cellValue)
            }
            if(type ==='money'){
                return this.numberFormat(cellValue)
            }
            return cellValue
        },
        /**
         * type이 date 인 cell은 Utc+9로 변경합니다
         * @param row
         * @returns {*}
         */
        dateFormatChange(date){
            if (!date) return undefined
            return DateTime.fromISO(DateTime.fromISO(date, {zone: 'utc'}).toISO()).toFormat("yyyy-MM-dd HH:mm")
        },
        /**
         * 돈과 연관되있는 데이터는 3번째 자리에서 ,를 찍어줍니다
         * @param number
         * @returns {string}
         */
        numberFormat(number){
            if(!number) return '0'
            return number.toLocaleString()
        },

        createRowKey(row) {
            const prefix = 'a-row'
            return prefix + '-' + this.itemKey && this.sortedItems[row][this.itemKey] || row
        },

        createCellKey(row, column, headerKey) {
            const prefix = this.createRowKey(row)
            return prefix + '-col-' + headerKey || column
        },
        onHeaderClicked(header) {
            this.onSortChanged(header.key)
        },

        onSortChanged(fieldName) {
            if (this.sortField === fieldName) {
                let prevDir = this.sortDir;
                if (prevDir === "desc") {
                    this.sortDir = "asc"
                } else {
                    this.sortDir = "desc"
                }
            } else {
                this.sortDir = "asc"
            }

            this.sortField = fieldName
        },
        internalSortFunction(a, b) {
            // 데이터에 키가 존재하지 않으면 정렬 불가.
            const hasA = Object.hasOwnProperty.call(a, this.sortField);
            const hasB = Object.hasOwnProperty.call(b, this.sortField);
            if (!hasA || !hasB) return 0

            // 두 값 중 하나라도 없으면 정렬 불가.
            const valueA = a[this.sortField];
            const valueB = b[this.sortField];
            if (!valueA || !valueB) return 0

            // 타입이 다르면 정렬 불가.
            const typeA = typeof valueA
            const typeB = typeof valueB
            if (typeA !== typeB) return 0

            // 값을 비교해서 정렬한다.
            if (typeA === 'number') {
                return valueA - valueB
            }
            // 그 외의 타입은 그냥 대소 비교.
            if (valueA > valueB) {
                return 1
            } else if (valueA < valueB) {
                return -1
            } else {
                return 0
            }
        }
    },
}
</script>

<style scoped>
.table-wrapper{
    height: 100%;
    overflow-y: auto;
}
.table-data{
    border-spacing: 0;
    overflow-x: auto;
}
.table-thead {
    position: sticky;
    top: 0;
    background-color: #dddddd;
    z-index: 1;
}

.table__thead__row__cell {
    font-size: 11px;
    padding-right: 8px;
    border: 1px solid #6f6f6f;
}
/*정렬시 header에 pointer 속성을 줍니다.*/
.sortable {
    cursor: pointer;
}
/*말줄임표를 사용할때*/
.short-text{
    text-overflow:ellipsis;
    overflow:hidden;
    white-space:nowrap;
}
/*cell에 버튼이나 고정된 길이에 데이터가 들어올때*/
.fist-cell-width{
    width:2%
}
</style>